import React, { Suspense } from "react"; // Убрал неиспользуемый useEffect
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import CompanySubscriptionGuard from "./components/auth/CompanySubscriptionGuard";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";


import LayoutRouter from "./components/layouts/LayoutRouter";

import AppToast from "./components/ui/AppToast";

import AdminLayout from "./components/layouts/AdminLayout";

import HomePage from "./pages/HomePage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import OnboardingPage from "./pages/auth/OnboardingPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

import MarketplacePage from "./pages/marketplace/MarketplacePage";
import ProductPublicPage from "./pages/marketplace/ProductPublicPage";
import BusinessPage from "./pages/BusinessPage";
import CompanyPublicPage from "./pages/CompanyPublicPage";
import CompaniesPage from "./pages/CompaniesPage";

import BookingPage from "./pages/BookingPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import ProfilePage from "./pages/ProfilePage";
import ChatPage from "./pages/ChatPage";

import ProtectedRoute from "./components/common/ProtectedRoute";

// Lazy admin pages
const PaymentSettings = React.lazy(
  () => import("./pages/admin/PaymentSettings"),
);
const PlanSettings = React.lazy(() => import("./pages/admin/PlanSettings"));
const AnalyticsPage = React.lazy(
  () => import("./pages/admin/analytics/AnalyticsPage"),
);
const CompaniesManagement = React.lazy(
  () => import("./pages/admin/CompaniesManagement"),
);
const CurrencyManagement = React.lazy(
  () => import("./pages/admin/CurrencyManagement"),
);
const PromptsManagement = React.lazy(
  () => import("./pages/admin/PromptsManagement"),
);
const ServiceCategoriesManagement = React.lazy(
  () => import("./pages/admin/ServiceCategoriesManagement"),
);
const BusinessTypesManagement = React.lazy(
  () => import("./pages/admin/BusinessTypesManagement"),
);
const PetTypesManagement = React.lazy(
  () => import("./pages/admin/PetTypesManagement"),
);
const CareersManagement = React.lazy(
  () => import("./pages/admin/CareersManagement"),
);
const PressManagement = React.lazy(
  () => import("./pages/admin/PressManagement"),
);
const BlogManagement = React.lazy(() => import("./pages/admin/BlogManagement"));
const PricingPage = React.lazy(() => import("./pages/PricingPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));

import BusinessLandingPage from "./pages/BusinessLandingPage";
import CareersPage from "./pages/CareersPage";
import PressPage from "./pages/PressPage";
import BlogPage from "./pages/BlogPage";

import CryptoPaymentPage from "./pages/payment/CryptoPaymentPage";
import PaymentSuccessPage from "./pages/payment/PaymentSuccessPage";
import PaymentFailedPage from "./pages/payment/PaymentFailedPage";

import CompanyDashboard from "./pages/company/CompanyDashboard";
import CompanyAnalyticsPage from "./pages/company/CompanyAnalyticsPage";
import EmployeeChatPage from "./pages/company/EmployeeChatPage";
import ServicesManagementPage from "./pages/company/ServicesManagementPage";
import AddonManagementPage from "./pages/company/AddonManagementPage";
import InventoryPage from "./pages/company/InventoryPage";
import EmployeesManagementPage from "./pages/company/EmployeesManagementPage";
import CalendarPage from "./pages/company/CalendarPage";
import CompanySettingsPage from "./pages/company/CompanySettingsPage";
import CompanyCustomersPage from "./pages/company/CompanyCustomersPage";
import CompanyBookingsPage from "./pages/company/CompanyBookingsPage";

import NotFoundPage from "./pages/NotFoundPage";

import "./styles/themes.css";
import "leaflet/dist/leaflet.css";
import EmployeeAnalytics from "./components/employees/EmployeeAnalytics";

function App() {
  return (
    <Router>
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
          <AppToast />
            <LayoutRouter>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center min-h-screen">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
                  </div>
                }
              >
                <Routes>
                  {/* PUBLIC */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />

                  <Route path="/marketplace" element={<MarketplacePage />} />
                  <Route
                    path="/marketplace/products/:productId"
                    element={<ProductPublicPage />}
                  />
                  <Route
                    path="/marketplace/services/:serviceId"
                    element={<ProductPublicPage />}
                  />
                  <Route path="/shop" element={<MarketplacePage />} />
                  <Route path="/services" element={<MarketplacePage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/about" element={<AboutPage />} />

                  <Route path="/companies" element={<CompaniesPage />} />
                  <Route path="/business" element={<BusinessPage />} />
                  <Route
                    path="/companies/:companyId"
                    element={<CompanyPublicPage />}
                  />

                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/press" element={<PressPage />} />
                  <Route path="/blog" element={<BlogPage />} />

                  {/* PAYMENT */}
                  <Route
                    path="/payment/crypto/:paymentId"
                    element={<CryptoPaymentPage />}
                  />
                  <Route
                    path="/payment/success"
                    element={<PaymentSuccessPage />}
                  />
                  <Route
                    path="/payment/failed"
                    element={<PaymentFailedPage />}
                  />

                  {/* PROTECTED USER ROUTES */}
                  <Route
                    path="/booking/:companyId?"
                    element={
                      <ProtectedRoute>
                        <BookingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/cart"
                    element={
                      <ProtectedRoute>
                        <CartPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute>
                        <CheckoutPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />

                  {/* COMPANY ROUTES */}
                  <Route
                    path="/company"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <CompanyDashboard />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/analytics"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <CompanyAnalyticsPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/calendar"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <CalendarPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/bookings"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <CompanyBookingsPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/chat"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <EmployeeChatPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/services"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <ServicesManagementPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/employees"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <EmployeeAnalytics />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/addons"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <AddonManagementPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/inventory"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <InventoryPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/settings"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <CompanySettingsPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/customers"
                    element={
                      <ProtectedRoute requiredRole="company_owner">
                        <CompanySubscriptionGuard>
                          <CompanyCustomersPage />
                        </CompanySubscriptionGuard>
                      </ProtectedRoute>
                    }
                  />

                  {/* ADMIN GROUPED ROUTES */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requiredRole="super_admin">
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AnalyticsPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route
                      path="payment-settings"
                      element={<PaymentSettings />}
                    />
                    <Route path="plan-settings" element={<PlanSettings />} />
                    <Route path="companies" element={<CompaniesManagement />} />
                    <Route path="currencies" element={<CurrencyManagement />} />
                    <Route path="prompts" element={<PromptsManagement />} />
                    <Route
                      path="service-categories"
                      element={<ServiceCategoriesManagement />}
                    />
                    <Route
                      path="business-types"
                      element={<BusinessTypesManagement />}
                    />
                    <Route path="pet-types" element={<PetTypesManagement />} />
                    <Route path="careers" element={<CareersManagement />} />
                    <Route path="press" element={<PressManagement />} />
                    <Route path="blog" element={<BlogManagement />} />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </LayoutRouter>

            <ChatPage />

            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: "#363636",
                  color: "#fff",
                },
              }}
            />
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
